import { ActionTree } from 'vuex';
import ApiUrl from '../../../api/url';
import { AccountInventoryResultDiffState } from './types';
import { RootState } from '../../types';
import * as types from './mutation-types';
import { isHappenedOtherPartyError } from '../../error-type';
import { AppContext } from '../../context';
import { translator } from '../../translator';
const MessageKey = AppContext.MessageKey;

export const actions: ActionTree<AccountInventoryResultDiffState, RootState> = {
    /**
     * アカウント棚卸結果一覧検索
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} pageNumber ページ番号
     * @param {any} perPage ページ毎の表示件数
     * @param {any} sort ソートするカラムと順番
     * @returns {Promise<void>}
     */
    async search({ dispatch, commit }, { searchItems = {}, pageNumber, perPage, sort } = {}) {
        const payload = {
            word: searchItems.word,
            start_date: searchItems.startDate == '' ? undefined : searchItems.startDate,
            end_date: searchItems.endDate == '' ? undefined : searchItems.endDate,
            page_number: pageNumber,
            per_page: perPage,
            'sort[column]': sort.column,
            'sort[direction]': sort.direction,
        };
        try {
            commit(types.SEARCH_START);
            const resData = await dispatch(
                'auth/api',
                { method: 'get', url: ApiUrl.AccountInventoryResultDiff.SEARCH, params: payload },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.SEARCH_SUCCESS, resData);
            }
        } catch (resData) {
            // アプリケーションエラー時
            commit(types.SEARCH_FAILURE, resData.errors[0]);
        }
    },

    /**
     * アカウント棚卸結果差分詳細取得
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {number} id アカウント棚卸結果差分ID
     * @returns {Promise<void>}
     */
    async show({ dispatch, commit }, id: number) {
        try {
            commit(types.SHOW_START);
            const resData = await dispatch(
                'auth/api',
                { method: 'get', url: ApiUrl.AccountInventoryResultDiff.SHOW.replace(':id', id.toString()) },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.SHOW_SUCCESS, resData);
            }
        } catch (resData) {
            // アプリケーションエラー時
            commit(types.SHOW_FAILURE, resData.errors[0]);
        }
    },

    /**
     * アカウント棚卸結果差分詳細 アカウント属性変更あり一覧検索
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} pageNumber ページ番号
     * @param {any} perPage ページ毎の表示件数
     * @param {any} sort ソートするカラムと順番
     * @param {number} id アカウント棚卸結果差分ID
     * @returns {Promise<void>}
     */
    async changedAccounts({ dispatch, commit }, { id, searchItems = {}, pageNumber, perPage, sort } = {}) {
        const payload = {
            word: searchItems.word,
            archive_node_types: searchItems.archive_node_types,
            changed_attribute: searchItems.changed_attribute,
            page_number: pageNumber,
            per_page: perPage,
            'sort[column]': sort.column,
            'sort[direction]': sort.direction,
        };
        try {
            commit(types.CHANGED_ACCOUNTS_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'get',
                    url: ApiUrl.AccountInventoryResultDiff.CHANGED_ACCOUNTS.replace(':id', id.toString()),
                    params: payload,
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.CHANGED_ACCOUNTS_SUCCESS, resData);
            }
        } catch (resData) {
            // アプリケーションエラー時
            commit(types.CHANGED_ACCOUNTS_FAILURE, resData.errors[0]);
        }
    },

    /**
     * アカウント棚卸結果差分詳細 アカウント追加一覧検索
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} pageNumber ページ番号
     * @param {any} perPage ページ毎の表示件数
     * @param {any} sort ソートするカラムと順番
     * @param {number} id アカウント棚卸結果差分ID
     * @returns {Promise<void>}
     */
    async addedAccounts({ dispatch, commit }, { id, searchItems = {}, pageNumber, perPage, sort } = {}) {
        const payload = {
            word: searchItems.word,
            archive_node_types: searchItems.archive_node_types,
            page_number: pageNumber,
            per_page: perPage,
            'sort[column]': sort.column,
            'sort[direction]': sort.direction,
        };
        try {
            commit(types.ADDED_ACCOUNTS_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'get',
                    url: ApiUrl.AccountInventoryResultDiff.ADDED_ACCOUNTS.replace(':id', id.toString()),
                    params: payload,
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.ADDED_ACCOUNTS_SUCCESS, resData);
            }
        } catch (resData) {
            // アプリケーションエラー時
            commit(types.ADDED_ACCOUNTS_FAILURE, resData.errors[0]);
        }
    },

    /**
     * アカウント棚卸結果差分詳細 アカウント削除一覧検索
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} pageNumber ページ番号
     * @param {any} perPage ページ毎の表示件数
     * @param {any} sort ソートするカラムと順番
     * @param {number} id アカウント棚卸結果差分ID
     * @returns {Promise<void>}
     */
    async deletedAccounts({ dispatch, commit }, { id, searchItems = {}, pageNumber, perPage, sort } = {}) {
        const payload = {
            word: searchItems.word,
            archive_node_types: searchItems.archive_node_types,
            page_number: pageNumber,
            per_page: perPage,
            'sort[column]': sort.column,
            'sort[direction]': sort.direction,
        };
        try {
            commit(types.DELETED_ACCOUNTS_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'get',
                    url: ApiUrl.AccountInventoryResultDiff.DELETED_ACCOUNTS.replace(':id', id.toString()),
                    params: payload,
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.DELETED_ACCOUNTS_SUCCESS, resData);
            }
        } catch (resData) {
            // アプリケーションエラー時
            commit(types.DELETED_ACCOUNTS_FAILURE, resData.errors[0]);
        }
    },

    /**
     * CSV出力
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @returns {Promise<void>}
     */
    async csv({ dispatch, commit, rootState }, id: number) {
        try {
            commit(types.CSV_OUTPUT_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'post',
                    url: ApiUrl.AccountInventoryResultDiff.CSV_OUTPUT.replace(':id', id.toString()),
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.CSV_OUTPUT_SUCCESS);

                // 成功トースト表示
                const i18n = (rootState as any).i18n;
                const message = translator(i18n, MessageKey.Csv.OUTPUT);
                dispatch('toastMessage', message, { root: true });
            }
        } catch (resData) {
            // アプリケーションエラー時
            const params = {
                errorTitle: resData.title,
                errorDetails: resData.errors,
            };
            commit(types.CSV_OUTPUT_FAILURE, params);
        }
    },

    /**
     * 画面切り替え時のstoreの消去
     * @param {any} commit Vuex変数
     * @returns {Promise<void>}
     */
    async clearOnBeforeRoute({ commit }) {
        commit(types.CLEAR_ALL);
    },
};
